import type {
  AdminUser,
  BusinessUnit,
  CreateBusinessUnit,
  CreateLegalEntity,
  CreateUserRequest,
  CreateUserResponse,
  DirectoryImport,
  EmployeeListResponse,
  ImportRun,
  ImportRunDetail,
  ImportSource,
  LegalEntity,
  OrgOverview,
  RoleGrant,
  RoleSummary,
  SupplierMatch,
  SupplierMatchListResponse,
  SupplierMatchResolve,
  SupplierMatchStatus,
  UpdateBusinessUnit,
  UpdateLegalEntity,
  UpdateUserRequest,
  UsagePeriod,
  UsageReport,
  UserListResponse,
  UserStatus,
} from "@procuremate/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authKeys } from "@/features/auth/api";
import { api } from "@/lib/api-client";

export interface UserListParams {
  q: string;
  status: UserStatus | "";
  page: number;
}

export const adminKeys = {
  all: ["admin"] as const,
  users: (params: UserListParams) => ["admin", "users", params] as const,
  user: (id: string) => ["admin", "user", id] as const,
  roles: ["admin", "roles"] as const,
  org: ["admin", "org"] as const,
  directoryImports: ["admin", "directory", "imports"] as const,
  employees: (params: { q: string; page: number }) => ["admin", "directory", "employees", params] as const,
  usage: (days: UsagePeriod) => ["admin", "usage", days] as const,
  importRuns: ["admin", "imports"] as const,
  importRun: (id: string) => ["admin", "imports", id] as const,
  supplierMatches: (params: { status: SupplierMatchStatus; page: number }) =>
    ["admin", "suppliers", "matches", params] as const,
};

/** GET /api/admin/usage — the usage & cost dashboard (WP-3.7, aggregates only). */
export function useUsage(days: UsagePeriod) {
  return useQuery({
    queryKey: adminKeys.usage(days),
    queryFn: () => api<UsageReport>(`/admin/usage?days=${days}`),
    placeholderData: keepPreviousData,
  });
}

export const USERS_PAGE_SIZE = 25;

export function useUsers(params: UserListParams) {
  const search = new URLSearchParams({ page: String(params.page), pageSize: String(USERS_PAGE_SIZE) });
  if (params.q) search.set("q", params.q);
  if (params.status) search.set("status", params.status);
  return useQuery({
    queryKey: adminKeys.users(params),
    queryFn: () => api<UserListResponse>(`/admin/users?${search.toString()}`),
    placeholderData: keepPreviousData,
  });
}

export function useUser(id: string) {
  return useQuery({ queryKey: adminKeys.user(id), queryFn: () => api<AdminUser>(`/admin/users/${id}`) });
}

export function useRoles() {
  return useQuery({ queryKey: adminKeys.roles, queryFn: () => api<RoleSummary[]>("/admin/roles"), staleTime: 60_000 });
}

export function useOrg() {
  return useQuery({ queryKey: adminKeys.org, queryFn: () => api<OrgOverview>("/admin/org") });
}

/** Any admin change can affect lists, details and — if admins edit themselves — the session user. */
function useAdminMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminKeys.all }),
        queryClient.invalidateQueries({ queryKey: authKeys.me }),
      ]);
    },
  });
}

export const useCreateUser = () =>
  useAdminMutation((body: CreateUserRequest) => api<CreateUserResponse>("/admin/users", { body }));

export const useUpdateUser = (id: string) =>
  useAdminMutation((body: UpdateUserRequest) => api<AdminUser>(`/admin/users/${id}`, { method: "PATCH", body }));

export type UserAction = "invite" | "deactivate" | "reactivate" | "sessions/revoke";
export const useUserAction = (id: string) =>
  useAdminMutation((action: UserAction) => api<unknown>(`/admin/users/${id}/${action}`, { method: "POST" }));

export const useGrantRole = (id: string) =>
  useAdminMutation((body: RoleGrant) => api<AdminUser>(`/admin/users/${id}/roles`, { body }));

export const useRevokeRole = (id: string) =>
  useAdminMutation((assignmentId: string) =>
    api<AdminUser>(`/admin/users/${id}/roles/${assignmentId}`, { method: "DELETE" }),
  );

export const useCreateLegalEntity = () =>
  useAdminMutation((body: CreateLegalEntity) => api<LegalEntity>("/admin/org/legal-entities", { body }));

export const useUpdateLegalEntity = () =>
  useAdminMutation(({ id, ...body }: UpdateLegalEntity & { id: string }) =>
    api<LegalEntity>(`/admin/org/legal-entities/${id}`, { method: "PATCH", body }),
  );

export const useCreateBusinessUnit = () =>
  useAdminMutation((body: CreateBusinessUnit) => api<BusinessUnit>("/admin/org/business-units", { body }));

export const useUpdateBusinessUnit = () =>
  useAdminMutation(({ id, ...body }: UpdateBusinessUnit & { id: string }) =>
    api<BusinessUnit>(`/admin/org/business-units/${id}`, { method: "PATCH", body }),
  );

// ── Directory (WP-2.5, ADR-0021) ─────────────────────────────────────────────

export const EMPLOYEES_PAGE_SIZE = 25;

export function useDirectoryImports() {
  return useQuery({
    queryKey: adminKeys.directoryImports,
    queryFn: () => api<DirectoryImport[]>("/admin/directory/imports"),
  });
}

export function useEmployees(params: { q: string; page: number }) {
  const search = new URLSearchParams({ page: String(params.page), pageSize: String(EMPLOYEES_PAGE_SIZE) });
  if (params.q) search.set("q", params.q);
  return useQuery({
    queryKey: adminKeys.employees(params),
    queryFn: () => api<EmployeeListResponse>(`/admin/directory/employees?${search.toString()}`),
    placeholderData: keepPreviousData,
  });
}

export const useImportDirectory = () =>
  useAdminMutation((file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api<DirectoryImport>("/admin/directory/imports", { form });
  });

// ── Imports & supplier queue (WP-3.3, ADR-0026) ─────────────────────────────

/** While a run is queued or running, its status is checked every few seconds. */
const RUN_POLL_MS = 3_000;
export const SUPPLIER_MATCHES_PAGE_SIZE = 20;

const unfinished = (run: ImportRun) => run.status === "QUEUED" || run.status === "RUNNING";

export function useImportRuns() {
  return useQuery({
    queryKey: adminKeys.importRuns,
    queryFn: () => api<ImportRun[]>("/admin/imports"),
    refetchInterval: (query) => (query.state.data?.some(unfinished) ? RUN_POLL_MS : false),
  });
}

export function useImportRun(id: string) {
  return useQuery({
    queryKey: adminKeys.importRun(id),
    queryFn: () => api<ImportRunDetail>(`/admin/imports/${id}`),
    refetchInterval: (query) => (query.state.data && unfinished(query.state.data) ? RUN_POLL_MS : false),
  });
}

/** Upload one file for a source (ADR-0026 adapters): purchase history or the vendor panel. */
export const useUploadImport = (source: ImportSource) =>
  useAdminMutation((file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api<ImportRun>(`/admin/imports/${source === "vendor_panel" ? "vendor-panel" : "purchase-history"}`, {
      form,
    });
  });

export function useSupplierMatches(params: { status: SupplierMatchStatus; page: number }) {
  const search = new URLSearchParams({
    status: params.status,
    page: String(params.page),
    pageSize: String(SUPPLIER_MATCHES_PAGE_SIZE),
  });
  return useQuery({
    queryKey: adminKeys.supplierMatches(params),
    queryFn: () => api<SupplierMatchListResponse>(`/admin/suppliers/match-queue?${search.toString()}`),
    placeholderData: keepPreviousData,
  });
}

export const useResolveSupplierMatch = () =>
  useAdminMutation(({ id, decision }: { id: string } & SupplierMatchResolve) =>
    api<SupplierMatch>(`/admin/suppliers/match-queue/${id}/resolve`, { method: "POST", body: { decision } }),
  );
